/**
 * Pull the "who / what" out of a request body so an audit entry can say
 * "User: test.user41 · Group: SG-Finance" instead of only the route.
 *
 * SAFETY IS THE WHOLE DESIGN. Request bodies carry plaintext and base64
 * passwords, SAS URLs, API keys and whole scripts, which is why the generic
 * audit entry never logged a body at all. So:
 *   - identity fields are taken only from an ALLOWLIST of known names;
 *   - any other field is kept only when it is a short, single-line primitive,
 *     its name is not secret-shaped, and its value is not a URL or a blob;
 *   - a secret-shaped NAME ("password", "tempPass", "clientSecret", "apiKey",
 *     "liveAzureScriptSas", "token", "credential", "pfx"…) is dropped whatever
 *     else is true of it, at every depth.
 *
 * Shared by the API (which stamps the result onto each generic entry) and the
 * Audit Logs tab (which reads the same labels out of the details of the
 * older, specifically-named entries).
 */
/** Never recorded, at any depth, whatever the value looks like. */
const SECRET_WORD = /^(pass\w*|pwd|secrets?|tokens?|credentials?|creds?|keys?|apikey|sas|sasurl|pfx|certs?|certificates?|otp|pin|auth\w*|cookies?|sessions?|signatures?|scripts?|commands?|content|html|base64|b64|connectionstring)$/;
/** Always secret, even run together in one lower-case word ("pfxpassword"). */
const SECRET_FRAGMENT = /password|secret|token|credential|base64|apikey/;
/**
 * Matched by WHOLE WORD of the field name ("liveAzureScriptSas" -> live,
 * azure, script, sas), so "Description" is not mistaken for a script and
 * "subscriptionId" is not mistaken for one either.
 */
function isSecretName(key) {
    if (SECRET_FRAGMENT.test(key.toLowerCase()))
        return true;
    const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").split(/[^A-Za-z0-9]+/).filter(Boolean);
    return words.some((w) => SECRET_WORD.test(w.toLowerCase()));
}
/**
 * Normalised field name (lower-case, no separators) -> the label it is shown
 * under. Order here is display order.
 */
const IDENTITY_FIELDS = [
    ["User", ["user", "userupn", "upn", "userprincipalname", "targetupn", "memberupn", "email", "useremail", "username", "samaccountname", "targetuser", "userdn", "memberdn", "targetdn"]],
    ["Member", ["member", "members", "memberids"]],
    ["Group", ["groupname", "group", "groupdn", "groupnames", "groups", "groupdisplayname"]],
    ["Groups removed", ["licensegroupsremoved", "groupsremoved", "removedgroups"]],
    ["Ticket", ["ticketnumber", "ticket", "ticketid", "sdpticket"]],
    ["Mailbox", ["mailbox", "identity", "mailboxidentity", "sharedmailbox", "primarysmtpaddress"]],
    ["Delegate", ["delegate", "delegateupn", "delegateuserupn", "trustee"]],
    ["Forward to", ["forwardingaddress", "forwardtoaddress", "forwardingsmtpaddress", "forwardto"]],
    ["Device", ["hostname", "devicename", "computername", "devices", "devicestowipe", "deviceid", "deviceids"]],
    ["Move to", ["targetou", "targetoudn", "oupath"]],
    ["Company", ["newcompany", "company"]],
    ["Office", ["newoffice", "office"]],
    ["Business", ["business", "businessname", "businesscode"]],
    ["Site", ["site", "sitename", "sitecode"]],
    ["Licence", ["licence", "license", "licensename", "licencename", "sku", "skuid", "skupartnumber", "licences", "licenses", "licensegroup"]],
    ["Policy", ["policy", "policyname", "retentionpolicy"]],
    ["Share", ["share", "sharename", "sharepath"]],
    ["Drive", ["drivename", "driveletter"]],
    ["Server", ["server", "servername"]],
    ["Printer", ["printername"]],
    ["Service", ["servicename"]],
    ["Software", ["softwarename", "appname", "packagename"]],
    ["Rule", ["rulename"]],
    ["Permission", ["permission", "accessrights", "role", "permissionlevel"]],
    ["New email", ["newemail", "emailaddress", "newemailaddress"]],
    ["Mode", ["mode"]],
    ["Enabled", ["enabled", "accountenabled"]],
    ["Name", ["name", "displayname"]],
    // A bare DN is a user on the user routes and a group on the group routes —
    // named neutrally rather than guessed.
    ["Target", ["dn", "distinguishedname"]]
];
/** field -> [label, rank]; rank is the field's position in its list, so a UPN outranks a DN. */
const LABEL_BY_FIELD = new Map();
for (const [label, fields] of IDENTITY_FIELDS)
    fields.forEach((f, rank) => { if (!LABEL_BY_FIELD.has(f))
        LABEL_BY_FIELD.set(f, [label, rank]); });
const LABEL_ORDER = IDENTITY_FIELDS.map(([label]) => label);
/** Fields that are only plumbing and say nothing an operator needs. */
const IGNORED_FIELDS = new Set([
    "refresh", "force", "confirm", "queue", "limit", "page", "offset", "timeoutms", "source", "grouptype",
    "membertype", "type", "format", "azureid", "memberazureid", "id", "objectid", "correlationid", "requestid"
]);
const MAX_VALUE = 200;
const MAX_CHANGE_VALUE = 80;
const MAX_LIST = 10;
const MAX_CHANGES = 12;
const norm = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, "");
/** A DN reads as its first RDN value: "CN=Test User,OU=…" -> "Test User". */
function readable(value) {
    const trimmed = value.trim();
    const cn = /^(?:CN|OU)=((?:\\,|[^,])+)/i.exec(trimmed);
    const shown = cn ? (cn[1] ?? trimmed).replace(/\\,/g, ",") : trimmed;
    return shown.length > MAX_VALUE ? `${shown.slice(0, MAX_VALUE)}…` : shown;
}
/** The best display name of a nested record ({ displayName, userPrincipalName, … }). */
function nameOf(record) {
    for (const key of ["displayName", "deviceName", "name", "userPrincipalName", "upn", "mail", "email", "groupName", "hostname", "dn"]) {
        const v = record[key];
        if (typeof v === "string" && v.trim())
            return readable(v);
    }
    return null;
}
function toDisplay(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === "string")
        return value.trim() ? readable(value) : null;
    if (typeof value === "number" || typeof value === "boolean")
        return String(value);
    if (Array.isArray(value)) {
        const names = value
            .map((item) => (item && typeof item === "object" ? nameOf(item) : toDisplay(item)))
            .filter((v) => Boolean(v));
        if (names.length === 0)
            return null;
        const shown = names.slice(0, MAX_LIST).join(", ");
        return names.length > MAX_LIST ? `${shown} (+${names.length - MAX_LIST} more)` : shown;
    }
    if (typeof value === "object")
        return nameOf(value);
    return null;
}
/** A value that could be a credential or a payload even under an innocent name. */
function unsafeChangeValue(value) {
    return value.length > MAX_CHANGE_VALUE || /[\r\n]/.test(value) || /:\/\//.test(value) || /^[A-Za-z0-9+/=_-]{40,}$/.test(value);
}
export function extractAuditSubject(input) {
    const subject = [];
    const changes = [];
    if (!input || typeof input !== "object" || Array.isArray(input))
        return { subject, changes };
    const byLabel = new Map();
    for (const [key, raw] of Object.entries(input)) {
        if (isSecretName(key))
            continue;
        const n = norm(key);
        if (IGNORED_FIELDS.has(n))
            continue;
        const hit = LABEL_BY_FIELD.get(n);
        if (hit) {
            const [label, rank] = hit;
            const shown = toDisplay(raw);
            if (!shown)
                continue;
            const list = byLabel.get(label) ?? [];
            if (!list.some((v) => v.value.toLowerCase() === shown.toLowerCase()))
                list.push({ rank, value: shown });
            byLabel.set(label, list);
            continue;
        }
        if (changes.length >= MAX_CHANGES)
            continue;
        if (typeof raw === "string") {
            const value = raw.trim();
            if (value && !unsafeChangeValue(value))
                changes.push({ label: key, value });
            else if (raw === "")
                changes.push({ label: key, value: "(cleared)" });
        }
        else if (typeof raw === "number" || typeof raw === "boolean") {
            changes.push({ label: key, value: String(raw) });
        }
    }
    for (const label of LABEL_ORDER) {
        const values = (byLabel.get(label) ?? []).sort((a, b) => a.rank - b.rank);
        if (values.length === 0)
            continue;
        // userUpn + userDN are two spellings of ONE person — show the best one
        // (the UPN) rather than reading as two users. Other labels can be plural.
        subject.push({ label, value: label === "User" ? (values[0]?.value ?? "") : values.map((v) => v.value).join(", ") });
    }
    return { subject, changes };
}
/** "User: test.user41 · Group: SG-Finance" — the one-line form of a subject. */
export function formatAuditSubject(parts) {
    return parts.map((p) => `${p.label}: ${p.value}`).join(" · ");
}
